import 'chartjs-adapter-moment';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { Button, Card, Col, Container, ListGroup, ListGroupItem, Pagination, Placeholder, Row } from "react-bootstrap";
import { useLocation, useNavigate } from 'react-router-dom';
import { callQuery, LLM_ASSESSMENT_DOWNLOAD, LLM_VISIT_FEEDBACK_DOWNLOAD, LLM_VISIT_FEEDBACK_SEARCH, PATIENT_VISIT_BY_CSN } from '../../../../amplify/api-gateway/query';
import { getLocalDateTimeStr, getSqlDateStrFromDateStr } from '../../../../utils/time';
import { DateType } from '../../../filters/dateTypes';
import { AdminFeedbackReportFilterBar } from './AdminFeedbackReportFilterBar';
import { PatientVisit } from '../../../../amplify/api-gateway/search-patients';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner } from '@fortawesome/free-solid-svg-icons';


export const FEEDBACK_COLUMNS = [
    {
        name: "Username",
        size: 4,
        id: "username"
    },
    {
        name: "Last Update Time",
        id: "update_time",
        size: 4,
    },
    {
        name: "CSN",
        id: "csn",
        size: 2,
    },
    {
        name: "MRN",
        id: "mrn",
        size: 2,
    },
    {
        name: "Feedback Target",
        id: "feedback_target",
        size: 2,
    },
    {
        name: "Feedback Type",
        id: "feedback_type",
        size: 2,
    },
    {
        name: "Note",
        id: "note",
        size: 4,
    },
];

const PAGE_SIZE = 30;

export const AdminFeedbackReport = (props: {
    user: any,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryModel = queryString.parse(location.search);

    const [filterDateOptions, setFilterDateOptions]: any = useState<any>({
        dateOptionType: DateType.Relative,
        relativeStart: 7,
        relativeStartScale: "d",
        relativeEnd: 0,
        relativeEndScale: "d",
        absoluteStart: queryModel["start"] ? new Date(Number(queryModel["start"])) : new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 7),
        absoluteEnd: queryModel["end"] ? new Date(Number(queryModel["end"])) : new Date(),
    })

    const [filterOptions, setFilterOptions]: any = useState<any>({
        "dimension": queryModel["dimension"] ? queryModel["dimension"] : 0,
        "period": queryModel["period"] ? queryModel["period"] : 1,
        "forecast_horizon": queryModel["forecast_horizon"] ? queryModel["forecast_horizon"] : 0,
        "display_options": queryModel["display_options"] ? queryModel["display_options"] : ["SHOW_FORECAST", "SHOW_FORECAST_BOUNDS", "SHOW_LAST_WEEK"],
    });

    const [isLoading, setIsLoading] = useState(false);
    const [activePageNumber, setActivePageNumber] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalNumberOfResults, setTotalNumberOfResults] = useState<number>(0);

    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [isDownloadingCohortAdmit, setIsDownloadingCohortAdmit] = useState<boolean>(false);
    const [isDownloadingCohortRevisit, setIsDownloadingCohortRevisit] = useState<boolean>(false);
    const [isDownloadingCohortEDD, setIsDownloadingCohortEDD] = useState<boolean>(false);
    const [columns, setColumns] = useState<any[]>(FEEDBACK_COLUMNS);
    const [data, setData] = useState<any[]>([]);

    useEffect(() => {
        refresh();
    }, [])

    const range = (start: number, stop: number, step: number) => {
        if (typeof stop == "undefined") {
            // one param defined
            stop = start;
            start = 0;
        }

        if (typeof step == "undefined") {
            step = 1;
        }

        if ((step > 0 && start >= stop) || (step < 0 && start <= stop)) {
            return [];
        }

        var result = [];
        for (var i = start; step > 0 ? i < stop : i > stop; i += step) {
            result.push(i);
        }

        return result;
    };

    const pageUpdate = (nextPageInput: number) => {
        let nextPage = nextPageInput;
        nextPage = Math.min(totalPages, nextPage);
        nextPage = Math.max(1, nextPage);
        setActivePageNumber(nextPage);
    };

    const updateFilterOptions = (option: string, val: any) => {
        setFilterOptions({
            ...filterOptions,
            [option]: val,
        });
    }

    const refresh = () => {
        setIsLoading(true);
        callQuery({
            operation: LLM_VISIT_FEEDBACK_SEARCH,
            start: getSqlDateStrFromDateStr(filterDateOptions.absoluteStart),
            end: getSqlDateStrFromDateStr(filterDateOptions.absoluteEnd),
            offset: (activePageNumber - 1) * PAGE_SIZE,
            limit: PAGE_SIZE,
        }).then((promiseOutput: any) => {
            const results = promiseOutput["results"];
            const totalNum = promiseOutput["total"];
            setData(results);
            const totalPages = Math.ceil(
                Number(totalNum) / PAGE_SIZE
            );
            setTotalPages(totalPages);
            setTotalNumberOfResults(totalNum);
            setIsLoading(false);
        });
    }

    const getLLMSection = (target: string) => {
        const section = target.split("/")[0].split("-")[0];
        const validSections = [
            "overview",
            "notes",
            "anatomy",
            "physiology",
            "risk",
        ];
        if (validSections.includes(section)) {
            return section;
        } else {
            return "overview";
        }
    }

    const openPatientPage = (row: any) => {
        callQuery({
            operation: PATIENT_VISIT_BY_CSN,
            csn: row["csn"],
        })
        .then((patients: PatientVisit[]) => {
            for (var patient of patients) {
                window.open(`/dashboard/${patient.internal_csn}/${getLLMSection(row["feedback_target"])}`, "_blank");
                break
            }
        });
    }

    const formatRowVal = (col: any, row: any) => {
        if (col.id === "feedback_target") {
            return getLLMSection(row[col.id]);
        } else if (col.id.endsWith("_time")) {
            return getLocalDateTimeStr(row[col.id]);
        } else {
            return row[col.id];
        }
    }

    const downloadFeedback = () => {
        setIsDownloading(true);
        callQuery({
            operation: LLM_VISIT_FEEDBACK_DOWNLOAD,
            start: getSqlDateStrFromDateStr(filterDateOptions.absoluteStart),
            end: getSqlDateStrFromDateStr(filterDateOptions.absoluteEnd),
            offset: 0,
            limit: 1_000_000,
        })
        .then((obj: any) => {
            setIsDownloading(false);
            window.open(obj.url, '_blank');
        });
    };

    const downloadAssessment = (jobType: string) => {
        if (jobType === "cohort_admit") {
            setIsDownloadingCohortAdmit(true);
        } else if (jobType === "cohort_revisit") {
            setIsDownloadingCohortRevisit(true);
        } else if (jobType === "cohort_edd") {
            setIsDownloadingCohortEDD(true);
        }
        callQuery({
            operation: LLM_ASSESSMENT_DOWNLOAD,
            job_type: jobType,
        })
        .then((obj: any) => {
            if (jobType === "cohort_admit") {
                setIsDownloadingCohortAdmit(false);
            } else if (jobType === "cohort_revisit") {
                setIsDownloadingCohortRevisit(false);
            } else if (jobType === "cohort_edd") {
                setIsDownloadingCohortEDD(false);
            }
            // console.log(obj);
            window.open(obj.url, '_blank');
        });
    };

    return (
        <div>
            <div className="metrics-sidebar" style={{overflowY: 'scroll'}}>
                <AdminFeedbackReportFilterBar
                    isLoading={isLoading}
                    filterOptions={filterOptions}
                    updateFilterOptions={updateFilterOptions}
                    filterDateOptions={filterDateOptions}
                    setFilterDateOptions={setFilterDateOptions}
                    refresh={refresh}
                    user={props.user}
                />
            </div>
            <Container fluid className="metrics-container">
                <Row className="mt-2 mb-2">
                    <Col className='text-right'>
                        <Button className="ms-2" onClick={() => { downloadAssessment("cohort_admit") }} size='sm' variant='outline-secondary' disabled={isDownloadingCohortAdmit}><FontAwesomeIcon icon={isDownloadingCohortAdmit ? faSpinner : faDownload} spin={isDownloadingCohortAdmit} /> Export Assessment Admit</Button>
                        <Button className="ms-2" onClick={() => { downloadAssessment("cohort_revisit") }} size='sm' variant='outline-secondary' disabled={isDownloadingCohortRevisit}><FontAwesomeIcon icon={isDownloadingCohortRevisit ? faSpinner : faDownload} spin={isDownloadingCohortRevisit} /> Export Assessment Revisit</Button>
                        <Button className="ms-2" onClick={() => { downloadAssessment("cohort_edd") }} size='sm' variant='outline-secondary' disabled={isDownloadingCohortEDD}><FontAwesomeIcon icon={isDownloadingCohortEDD ? faSpinner : faDownload} spin={isDownloadingCohortEDD} /> Export Assessment EDD</Button>
                    </Col>
                </Row>
                <Row className="mt-2 mb-2">
                    <Col className='text-right'>
                        <Button onClick={downloadFeedback} size='sm' variant='secondary' disabled={isDownloading}><FontAwesomeIcon icon={isDownloading ? faSpinner : faDownload} spin={isDownloading} /> Download Feedback</Button>
                    </Col>
                </Row>
                <Row className="mt-2 mb-2">
                    <Col>
                        <Card className="trackboard-table">
                            {isLoading ? (
                                <Row className="m-4">
                                    <Col>
                                        {[1, 2, 3].map((v) => (
                                            <Row>
                                                <Col>
                                                    <Placeholder
                                                        key={v}
                                                        xs={6}
                                                    />
                                                </Col>
                                            </Row>
                                        ))}
                                    </Col>
                                </Row>
                            ) : (
                                <>
                                    <ListGroup variant="flush">
                                        <ListGroupItem className="d-flex flex-column trackboard-table-header">
                                            <div
                                                style={{
                                                    display: "block",
                                                    width: "100%",
                                                }}
                                            >
                                                {columns.map((col) => {
                                                    const totalRelativeCols =
                                                        columns.reduce(
                                                            (acc, obj) =>
                                                                acc + (obj.size ?? 0),
                                                            0
                                                        );
                                                    return (
                                                        <div
                                                            key={col.id}
                                                            style={{
                                                                display:
                                                                    "inline-block",
                                                                cursor: "pointer",
                                                                width: `${
                                                                    (100 *
                                                                        col.size) /
                                                                    totalRelativeCols
                                                                }%`,
                                                            }}
                                                            onClick={() => {
                                                                // sortPatients(col.id)
                                                            }}
                                                        >
                                                            {col.name}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </ListGroupItem>
                                        {
                                            data && data.map(row => (
                                                <ListGroupItem className="d-flex flex-column" onClick={() => {
                                                    openPatientPage(row);
                                                }}>
                                                    <div
                                                        style={{
                                                            display: "block",
                                                            width: "100%",
                                                        }}
                                                    >
                                                        {columns.map((col) => {
                                                            const totalRelativeCols =
                                                                columns.reduce(
                                                                    (acc, obj) =>
                                                                        acc + (obj.size ?? 0),
                                                                    0
                                                                );
                                                            return (
                                                                <div
                                                                    key={col.id}
                                                                    style={{
                                                                        display: "inline-block",
                                                                        cursor: "pointer",
                                                                        width: `${
                                                                            (100 * col.size) / totalRelativeCols
                                                                        }%`,
                                                                    }}
                                                                >
                                                                    {
                                                                        formatRowVal(col, row)
                                                                    }
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </ListGroupItem>
                                            ))
                                        }
                                    </ListGroup>
                                </>
                            )}
                        </Card>
                    </Col>
                </Row>
                {
                    !isLoading && (
                        <Row>
                            <Col md={8}>
                                <Pagination>
                                    <Pagination.First
                                        onClick={() => setActivePageNumber(1)}
                                    />
                                    <Pagination.Prev
                                        onClick={() =>
                                            setActivePageNumber(
                                                Math.max(1, activePageNumber - 1)
                                            )
                                        }
                                    />

                                    {totalPages < 6 ? (
                                        <>
                                            {range(1, totalPages + 1, 1).map(
                                                (idx: number) => {
                                                    if (idx === activePageNumber)
                                                        return (
                                                            <Pagination.Item
                                                                key={idx}
                                                                active
                                                                onClick={() =>
                                                                    pageUpdate(idx)
                                                                }
                                                            >
                                                                {idx}
                                                            </Pagination.Item>
                                                        );
                                                    else
                                                        return (
                                                            <Pagination.Item
                                                                key={idx}
                                                                onClick={() =>
                                                                    pageUpdate(idx)
                                                                }
                                                            >
                                                                {idx}
                                                            </Pagination.Item>
                                                        );
                                                }
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {activePageNumber - 4 > 1 && (
                                                <Pagination.Ellipsis />
                                            )}
                                            {range(
                                                Math.max(1, activePageNumber - 4),
                                                Math.min(
                                                    totalPages,
                                                    activePageNumber + 5
                                                ),
                                                1
                                            ).map((idx: number) => {
                                                if (idx === activePageNumber)
                                                    return (
                                                        <Pagination.Item
                                                            key={idx}
                                                            active
                                                            onClick={() =>
                                                                pageUpdate(idx)
                                                            }
                                                        >
                                                            {idx}
                                                        </Pagination.Item>
                                                    );
                                                else
                                                    return (
                                                        <Pagination.Item
                                                            key={idx}
                                                            onClick={() =>
                                                                pageUpdate(idx)
                                                            }
                                                        >
                                                            {idx}
                                                        </Pagination.Item>
                                                    );
                                            })}
                                            {activePageNumber + 5 < totalPages + 1 && (
                                                <Pagination.Ellipsis />
                                            )}
                                        </>
                                    )}
                                    <Pagination.Next
                                        onClick={() =>
                                            pageUpdate(
                                                Math.min(
                                                    totalPages,
                                                    activePageNumber + 1
                                                )
                                            )
                                        }
                                    />
                                    <Pagination.Last
                                        onClick={() =>
                                            pageUpdate(Math.max(totalPages, 1))
                                        }
                                    />
                                </Pagination>
                            </Col>
                            <Col md={4} className='text-right text-muted'>
                                Results Found: {totalNumberOfResults}
                            </Col>
                        </Row>
                    )
                }
            </Container>
        </div>
    );
};
